import BaseService from './BaseService';
import apiClient from '../plugins/axios';

/**
 * Agent Stage & Prompt Version Service
 * Swagger: /agent/{agentId}/stage, /agent/{agentId}/stage/{stageId}/prompt
 */
class AgentStageService extends BaseService {
  constructor() {
    super('agent');
  }

  /**
   * Agent'ın stage'lerini listele (her biri kendi prompt versiyonlarıyla)
   * @param {string} agentId
   * @returns {Promise} data: GetAgentStageResponse[]
   */
  async listStages(agentId) {
    try {
      const response = await apiClient.get(`/agent/${agentId}/stage`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Yeni stage oluştur (ilk prompt versiyonuyla birlikte, v1 otomatik aktif)
   * @param {string} agentId
   * @param {Object} body - { name, position?, is_initial?, initial_prompt_content }
   * @returns {Promise} data: CreateResponseModel { id }
   */
  async createStage(agentId, body) {
    try {
      const response = await apiClient.post(`/agent/${agentId}/stage`, body);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Stage metadata'sını güncelle (name, position, is_initial)
   * @param {string} agentId
   * @param {string} stageId
   * @param {Object} body - { name, position?, is_initial? }
   * @returns {Promise}
   */
  async updateStage(agentId, stageId, body) {
    try {
      const response = await apiClient.put(`/agent/${agentId}/stage/${stageId}`, body);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Stage'i ve tüm prompt versiyonlarını sil (agent'ın son stage'i silinemez)
   * @param {string} agentId
   * @param {string} stageId
   * @returns {Promise}
   */
  async deleteStage(agentId, stageId) {
    try {
      const response = await apiClient.delete(`/agent/${agentId}/stage/${stageId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Stage'in tüm prompt versiyonlarını listele (en yeni en üstte)
   * @param {string} agentId
   * @param {string} stageId
   * @returns {Promise} data: GetAgentStagePromptResponse[]
   */
  async listPromptVersions(agentId, stageId) {
    try {
      const response = await apiClient.get(`/agent/${agentId}/stage/${stageId}/prompt`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Yeni prompt versiyonu ekle (versiyon numarası sunucu tarafında hesaplanır, otomatik aktif olmaz)
   * @param {string} agentId
   * @param {string} stageId
   * @param {Object} body - { content }
   * @returns {Promise} data: CreateResponseModel { id }
   */
  async createPromptVersion(agentId, stageId, body) {
    try {
      const response = await apiClient.post(`/agent/${agentId}/stage/${stageId}/prompt`, body);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Belirtilen prompt versiyonunu aktif et (diğer tüm versiyonlar otomatik pasifleşir)
   * @param {string} agentId
   * @param {string} stageId
   * @param {string} promptId
   * @returns {Promise}
   */
  async activatePromptVersion(agentId, stageId, promptId) {
    try {
      const response = await apiClient.patch(
        `/agent/${agentId}/stage/${stageId}/prompt/${promptId}/activate`,
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * İki versiyon arasındaki unified diff'i getir
   * @param {string} agentId
   * @param {string} stageId
   * @param {number} fromVersion
   * @param {number} toVersion
   * @returns {Promise} data: PromptDiffResponse { from_version, to_version, diff }
   */
  async getPromptDiff(agentId, stageId, fromVersion, toVersion) {
    try {
      const params = { from: fromVersion, to: toVersion };
      const response = await apiClient.get(`/agent/${agentId}/stage/${stageId}/prompt/diff`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Stage'in her prompt versiyonu için kaç farklı aramada kullanıldığını getir
   * @param {string} agentId
   * @param {string} stageId
   * @returns {Promise} data: PromptVersionUsageResponse[]
   */
  async getPromptUsage(agentId, stageId) {
    try {
      const response = await apiClient.get(`/agent/${agentId}/stage/${stageId}/prompt/usage`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}

export default new AgentStageService();
